/**
 * One reference-letter draft and everything the reviewer changed on it.
 *
 * Nothing is stored server-side: every change re-sends the whole set of overrides and the proxy
 * rebuilds the draft. Two rules keep the file and the preview the same letter:
 *   - the task lines the first draft came back with are sent back, WITH their source, from then
 *     on — so the model runs once per letter, and a fallback to the supervisor's own words stays
 *     flagged on every later call;
 *   - the download sends the exact request that produced the draft on screen, never a newer one
 *     still waiting for its debounce.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { downloadReferenceLetterDocx, fetchReferenceLetterDraft } from '../../services/referenceLetterApi';
import type { DraftOverrides, DraftRequestBody, ReferenceLetterDraft } from '../../types/referenceLetterDraft';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';

const DEBOUNCE_MS = 350;
type Seeded = { tasks: string[]; source: ReferenceLetterDraft['taskSource'] };

/** The caller keys the workspace by `recordId`, so another request starts from fresh state. */
export function useReferenceLetterDraft(recordId: string) {
  const [overrides, setOverrides] = useState<DraftOverrides>({ choices: {}, values: {}, confirmed: [] });
  const [template, setTemplate] = useState<string | undefined>(undefined);
  const [shown, setShown] = useState<{ draft: ReferenceLetterDraft; request: DraftRequestBody } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstTaskSource, setFirstTaskSource] = useState<Seeded['source'] | null>(null);
  const [downloading, setDownloading] = useState(false);
  // What the reviewer has asked for, as a key from state alone, and the key the last timer sent:
  // while they differ a change waits for its debounce and the draft on screen is about to be
  // replaced (`pending`).
  const wanted = useMemo(() => JSON.stringify([recordId, template, overrides]), [recordId, template, overrides]);
  const [sent, setSent] = useState<string | null>(null);
  const seeded = useRef<Seeded | undefined>(undefined);
  const sequence = useRef(0);

  const body = useCallback((): DraftRequestBody => {
    const tasks = overrides.tasks ?? seeded.current?.tasks;
    const taskSource = overrides.tasks ? 'reviewer' : seeded.current?.source;
    return { recordId, template, overrides: { ...overrides, ...(tasks && { tasks, taskSource }) } };
  }, [recordId, template, overrides]);

  useEffect(() => {
    const request = body();
    const key = wanted;
    const id = ++sequence.current;
    const first = !seeded.current;
    const timer = window.setTimeout(async () => {
      setSent(key);
      setBusy(true);
      try {
        const next = await fetchReferenceLetterDraft(request);
        if (id !== sequence.current) return; // a newer change is on its way
        if (first) {
          seeded.current = { tasks: next.tasks, source: next.taskSource };
          setFirstTaskSource(next.taskSource);
          void logAnalyticsEvent('reference_letters_draft_opened', {
            template: next.template,
            blocking: next.flags.filter((f) => f.level === 'blocking').length,
            to_confirm: next.flags.filter((f) => f.level === 'confirm').length,
            task_source: next.taskSource,
          });
        }
        // The draft on screen was built from these task lines; the download must send them too, or
        // the proxy would draft the tasks again and the file could differ from the preview.
        const shownRequest = first
          ? { ...request, overrides: { ...request.overrides, tasks: next.tasks, taskSource: next.taskSource } }
          : request;
        setShown({ draft: next, request: shownRequest });
        setError(null);
      } catch (err) {
        if (id === sequence.current) setError(err instanceof Error ? err.message : 'Boris could not draft this letter');
      } finally {
        if (id === sequence.current) setBusy(false);
      }
    }, first ? 0 : DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `wanted` changes exactly when `body` does (both follow recordId, template and overrides), so
    // listing it adds no run.
  }, [body, wanted]);

  const choose = (key: string, label: string) => setOverrides((o) => ({ ...o, choices: { ...o.choices, [key]: label } }));
  const setValue = (name: string, value: string) => setOverrides((o) => ({ ...o, values: { ...o.values, [name]: value } }));
  const setGender = (gender: 'male' | 'female') => setOverrides((o) => ({ ...o, gender }));
  const setTasks = (tasks: string[]) => setOverrides((o) => ({ ...o, tasks }));
  const confirm = (token: string, checked: boolean) =>
    setOverrides((o) => ({ ...o, confirmed: checked ? [...new Set([...o.confirmed, token])] : o.confirmed.filter((t) => t !== token) }));
  const switchTemplate = (name: string) => {
    // Paragraphs, fields and ticks belong to a template (an interim letter has no end date); the
    // gender and the reviewer's own task lines do not. Unedited task lines are drafted afresh,
    // because the new template may be in the other language.
    if (!overrides.tasks) seeded.current = undefined;
    setOverrides((o) => ({ choices: {}, values: {}, confirmed: [], gender: o.gender, tasks: o.tasks }));
    setTemplate(name);
  };

  /** Downloads the .docx of the request behind the draft on screen, named as the proxy names it. */
  const download = async () => {
    if (!shown) return;
    setDownloading(true);
    try {
      const blob = await downloadReferenceLetterDocx(shown.request);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = shown.draft.fileName;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      void logAnalyticsEvent('reference_letters_draft_downloaded', {
        template: shown.draft.template,
        paragraphs_changed: Object.keys(shown.request.overrides.choices).length,
        fields_changed: Object.keys(shown.request.overrides.values).length,
        tasks_edited: shown.request.overrides.taskSource === 'reviewer',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return {
    // `pending`: a change is waiting for its debounce — the draft on screen no longer reflects it.
    draft: shown?.draft ?? null, loading: busy, pending: sent !== wanted, error, firstTaskSource, downloading,
    // The request the draft on screen came from: what Send for signature sends, like the download.
    request: shown?.request ?? null,
    choose, setValue, setGender, setTasks, confirm, switchTemplate, download,
  };
}
